import {CSSProperties} from 'react'

import Colors from '@utils/Colors.js'

import {radius, shadows, hoverOverlay, statusColors} from '../styles.js'

// All button components (Button, IconButton, SplitButton, MenuButton, Link) take their look from here: sizes, spacing
// and the colours of each variant and state. Change a value here instead of in a component, so every button stays alike.

// 'nav': a button of the courier app's bottom nav bar, no background, its colour shows which one is selected
export type ButtonVariant = 'solid' | 'outline' | 'outline-white' | 'text' | 'error' | 'nav'
export type ButtonSize = 'md' | 'lg' | 'xl'

export const buttonSizes = {
    // Height of the button per size: md is the default, lg the size with the label below the icon, xl the large buttons
    // of the courier app
    height: {md: 36, lg: 48, xl: 60} as Record<ButtonSize, number>,
    paddingX: 12,
    radius: radius.default,
    fontSize: 14,
    // A button whose content isn't a string (an icon as children) shows it at the icon size
    iconSize: 18,
    // Between the icon and the label
    iconGap: 12,
    fontWeight: 500,
    letterSpacing: 0.25,
    // SplitButton: the padding on the side where the two halves meet, and right of the arrow
    splitInnerPadding: 6,
    splitArrowPaddingRight: 3,
    // The 'nav' variant: a larger icon above a plain label, with little padding so five fit on a phone
    nav: {
        iconSize: 24,
        paddingX: 6,
        fontWeight: 400,
        letterSpacing: 'normal'
    }
}

export const iconButtonSizes = {
    size: 36,
    iconSize: 20,
    radius: radius.round,
    // The number badge at the top right of the icon
    badgeSize: 18,
    badgeTop: -8,
    badgeLeft: 22,
    // Badges above this show the 9+ icon
    badgeMax: 9
}

// The browser styles of <a> and <button> that would change the look of something that is clicked but doesn't look
// like a button: Link, the DividerHeader toggle, Info, a clickable Icon
export const buttonResetStyle: CSSProperties = {
    display: 'inline-block',
    marginTop: 0,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    padding: 0,
    border: 'none',
    background: 'none',
    textAlign: 'inherit',
    color: 'inherit',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    letterSpacing: 'inherit',
    lineHeight: 'inherit'
}

export const linkStyle = {
    fontSize: 14,
    fontWeight: 'bold' as const,
    letterSpacing: 0.4,
    lineHeight: 1.5
}

export const buttonColors = {
    solidBackground: Colors.buttonSolid,
    solidText: Colors.white,
    // Solid on a caller's colour: dark text on a light colour
    solidTextOnLight: Colors.textDark,
    solidDisabledBackground: Colors.textMedium,
    errorBackground: statusColors.error.dark,
    errorText: Colors.white,
    outlineBackground: Colors.backgroundNeutral,
    outlineWhiteBackground: Colors.backgroundWhite,
    outlineDisabledBackground: Colors.backgroundSelected,
    text: Colors.textDark,
    textDisabled: Colors.textMedium,
    // Border of a selected outline or text button
    selectedBorder: Colors.buttonSolid,
    shadow: shadows.raised,
    // The 'nav' variant: the selected one darker, a disabled one faded
    nav: Colors.textMedium,
    navSelected: Colors.textDark,
    navDisabledOpacity: 0.3,

    iconButton: Colors.buttonSolid,
    iconButtonDisabled: Colors.grey40,
    iconButtonHover: hoverOverlay.default,
    badge: Colors.errorBright,

    link: Colors.buttonSolid,
    linkDisabled: Colors.grey60
}

interface ButtonState {
    variant: ButtonVariant
    /** Background of a solid button instead of the default grey, the text turns dark on a light colour */
    color?: string
    disabled?: boolean
    selected?: boolean
}

// Text colour of a solid button on the given background colour
function getSolidTextColor(color?: string) {
    if (!color) {
        return buttonColors.solidText
    }

    return Colors.isDarkBackground(color) ? buttonColors.solidText : buttonColors.solidTextOnLight
}

/** Background, text colour, border and shadow of a Button for its variant and state */
export function getButtonColors({variant, color, disabled, selected}: ButtonState): CSSProperties {
    const selectedBorder = selected ? `1px solid ${buttonColors.selectedBorder}` : 'none'

    switch (variant) {
        case 'error':
            return {
                background: disabled ? buttonColors.solidDisabledBackground : buttonColors.errorBackground,
                color: buttonColors.errorText,
                border: 'none',
                boxShadow: buttonColors.shadow
            }
        case 'outline':
        case 'outline-white':
            return {
                background: disabled ? buttonColors.outlineDisabledBackground : variant === 'outline' ? buttonColors.outlineBackground : buttonColors.outlineWhiteBackground,
                color: disabled ? buttonColors.textDisabled : buttonColors.text,
                border: selectedBorder,
                boxShadow: buttonColors.shadow
            }
        case 'nav':
            return {
                background: 'transparent',
                color: selected ? buttonColors.navSelected : buttonColors.nav,
                border: 'none',
                opacity: disabled ? buttonColors.navDisabledOpacity : undefined
            }
        case 'text':
            return {
                background: 'transparent',
                color: disabled ? buttonColors.textDisabled : buttonColors.text,
                border: selectedBorder
            }
        case 'solid':
        default:
            return {
                background: disabled ? buttonColors.solidDisabledBackground : (color || buttonColors.solidBackground),
                color: getSolidTextColor(color),
                border: 'none',
                boxShadow: buttonColors.shadow
            }
    }
}

/** Colour of the loading Spinner in a Button: the button's text colour, so it shows on the solid colours too */
export function getSpinnerColor({variant, color}: Pick<ButtonState, 'variant' | 'color'>) {
    if (variant === 'error') {
        return buttonColors.errorText
    }

    if (variant === 'solid' || !variant) {
        return getSolidTextColor(color)
    }

    return buttonColors.text
}

/**
 * Corners of one half of a split button (SplitButton, or two buttons placed against each other): the half at the left
 * keeps its left corners, the one at the right its right corners
 */
export function getSplitRadius(side: 'left' | 'right'): string {
    const r = `${buttonSizes.radius}px`
    return side === 'left' ? `${r} 0px 0px ${r}` : `0px ${r} ${r} 0px`
}
